import { getAccessToken, getSupabaseFunctionUrl } from "@/lib/supabase";

export interface ApiKeyMetadata {
  id: string;
  name: string;
  key_prefix: string;
  is_active: boolean;
  expires_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreatedApiKey {
  id: string;
  name: string;
  key_prefix: string;
  api_key: string;
  expires_at: string;
  created_at: string;
}

export interface MeApiKeyResponse {
  id: string;
  name: string;
  email: string;
}

interface ApiResponse<T> {
  data?: T;
  error?: string;
  message?: string;
}

async function apiKeyFetch<T>(path = "", init: RequestInit = {}): Promise<T> {
  const token = await getAccessToken();
  if (!token) {
    throw new Error("You must be signed in to manage API keys.");
  }

  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);
  headers.set("Content-Type", "application/json");

  const response = await fetch(getSupabaseFunctionUrl(`api-keys${path}`), {
    ...init,
    headers,
  });

  const payload = (await response.json().catch(() => ({}))) as ApiResponse<T>;
  if (!response.ok) {
    throw new Error(payload.error ?? payload.message ?? "API key request failed.");
  }

  return payload.data as T;
}

export const apiKeys = {
  list: () => apiKeyFetch<ApiKeyMetadata[]>(),
  create: (name: string) =>
    apiKeyFetch<CreatedApiKey>("", {
      method: "POST",
      body: JSON.stringify({ name }),
    }),
  getMeUrl: () => getSupabaseFunctionUrl("capacitor-api/me"),
  // The MCP server and llms.txt are hosted by the app itself (not Supabase), so
  // they live on the app origin. Falls back to a relative path during SSR.
  getMcpUrl: () => (typeof window !== "undefined" ? `${window.location.origin}/mcp` : "/mcp"),
  getLlmsTxtUrl: () => (typeof window !== "undefined" ? `${window.location.origin}/llms.txt` : "/llms.txt"),
  testMe: async (apiKey: string) => {
    const response = await fetch(getSupabaseFunctionUrl("capacitor-api/me"), {
      method: "GET",
      headers: {
        Authorization: `Bearer ${apiKey}`,
      },
    });

    const payload = (await response.json().catch(() => ({}))) as {
      data?: MeApiKeyResponse;
      // The data API nests errors as { error: { code, message } }.
      error?: string | { code?: string; message?: string };
      message?: string;
    };
    if (!response.ok) {
      const errorMessage = typeof payload.error === "string" ? payload.error : payload.error?.message;
      throw new Error(errorMessage ?? payload.message ?? "API key test failed.");
    }

    return payload.data as MeApiKeyResponse;
  },
  revoke: (id: string) =>
    apiKeyFetch<Pick<ApiKeyMetadata, "id" | "revoked_at" | "is_active">>(`/${id}/revoke`, {
      method: "POST",
      body: JSON.stringify({}),
    }),
};
